import {
  CaretRight,
  ClipboardText,
  EnvelopeSimple,
  FileText,
  Key,
  PaintBrushBroad,
  SlidersHorizontal,
  TextAlignLeft,
  UsersThree,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui-kit/card';
import { cn } from '@/lib/utils';

type SettingsCardItem = {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: typeof UsersThree;
};

const WORKSPACE_ITEMS: SettingsCardItem[] = [
  {
    id: 'users-roles',
    title: 'Users & roles',
    description: 'Who can sign in, and what they can do.',
    path: '/identity-management',
    icon: UsersThree,
  },
  {
    id: 'custom-fields',
    title: 'Custom fields',
    description: 'Manage custom attributes captured on contacts.',
    path: '/custom-fields',
    icon: SlidersHorizontal,
  },
];

const SENDING_ITEMS: SettingsCardItem[] = [
  {
    id: 'double-opt-in',
    title: 'Double opt-in',
    description: 'Require confirmation before contacts become sendable.',
    path: '/settings/opt-in',
    icon: FileText,
  },
  {
    id: 'system-emails',
    title: 'System emails',
    description: 'Edit transactional messages sent by the platform.',
    path: '/settings/system-emails',
    icon: EnvelopeSimple,
  },
];

const COMPLIANCE_ITEMS: SettingsCardItem[] = [
  {
    id: 'branding',
    title: 'Branding',
    description: 'Style the public unsubscribe and confirmation pages.',
    path: '/settings/branding',
    icon: PaintBrushBroad,
  },
  {
    id: 'legal-footer',
    title: 'Legal footer',
    description: 'Set the sender identity appended to marketing emails.',
    path: '/settings/legal-footer',
    icon: TextAlignLeft,
  },
  {
    id: 'audit-log',
    title: 'Audit log',
    description: 'Every write this organisation has made, newest first.',
    path: '/audit-log',
    icon: ClipboardText,
  },
];

const SettingsCard = ({ title, description, path, icon: Icon }: SettingsCardItem) => {
  const navigate = useNavigate();

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => navigate(path)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(path);
        }
      }}
      className={cn(
        'cursor-pointer transition-colors hover:border-primary/40 hover:bg-neutral-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <CardContent className="flex items-start gap-4">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon size={18} weight="regular" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="font-semibold text-high-emphasis">{title}</p>
          <p className="truncate text-sm text-muted-foreground">{description}</p>
        </div>
        <CaretRight size={16} className="mt-1.5 shrink-0 text-neutral-400" />
      </CardContent>
    </Card>
  );
};

export const SettingsPage = () => {
  return (
    <div className="flex h-full w-full max-w-[1400px] flex-col gap-6 md:gap-8">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-high-emphasis">Settings</h2>
        <p className="text-sm text-muted-foreground">Workspace configuration.</p>
      </div>
      <div className="flex flex-col gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Sending</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {SENDING_ITEMS.map((item) => (
            <SettingsCard key={item.id} {...item} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Compliance</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {COMPLIANCE_ITEMS.map((item) => (
            <SettingsCard key={item.id} {...item} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Workspace</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {WORKSPACE_ITEMS.map((item) => (
            <SettingsCard key={item.id} {...item} />
          ))}
        </div>
      </div>
      <>
        <div className="flex flex-col gap-3">
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Developers</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SettingsCard
              id="api-keys"
              title="API keys"
              description="Manage credentials for your integrations."
              path="/api-keys"
              icon={Key}
            />
          </div>
        </div>
      </>
    </div>
  );
};
